import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { DataGrid } from "@mui/x-data-grid";
import { bulkDeleteOrders, deleteOrder, getOrders } from "../../api/adminService";
import { OrderStatusColors, OrderStatusLabels, formatCurrency, formatDate } from "./constants";

const WIPED_ITEMS = [
  "Order items",
  "Payments",
  "Delivery record",
  "Fee / commission record",
  "Coupon usage record",
];
const UNLINKED_ITEMS = ["Product reviews (kept, just unlinked from the order)", "Chat messages (kept, just unlinked from the order)"];

export default function DeleteOrders() {
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [selectionModel, setSelectionModel] = useState({ type: "include", ids: new Set() });
  const selectedIds = useMemo(() => Array.from(selectionModel.ids), [selectionModel]);

  const [target, setTarget] = useState(null); // { mode: "single", order } | { mode: "bulk", orderIds }
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [dialogError, setDialogError] = useState("");
  const [resultAlert, setResultAlert] = useState(null);

  function loadOrders() {
    setLoading(true);
    getOrders({
      status: statusFilter || undefined,
      search: search || undefined,
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
    })
      .then((data) => {
        setRows(data.items);
        setRowCount(data.totalCount);
        setError("");
      })
      .catch(() => setError("Could not load orders. Is the API running?"))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, paginationModel]);

  function handleSearchSubmit(e) {
    e.preventDefault();
    setPaginationModel((p) => ({ ...p, page: 0 }));
    loadOrders();
  }

  function openSingleDelete(order) {
    setResultAlert(null);
    setDialogError("");
    setConfirmText("");
    setTarget({ mode: "single", order });
  }

  function openBulkDelete() {
    setResultAlert(null);
    setDialogError("");
    setConfirmText("");
    setTarget({ mode: "bulk", orderIds: selectedIds });
  }

  function closeDialog() {
    if (deleting) return;
    setTarget(null);
    setConfirmText("");
    setDialogError("");
  }

  const expectedConfirmText = target?.mode === "single" ? target.order.orderNumber : "DELETE";
  const canConfirm = target && confirmText.trim() === expectedConfirmText && !deleting;

  async function handleConfirmDelete() {
    if (!target) return;
    setDeleting(true);
    setDialogError("");
    try {
      if (target.mode === "single") {
        await deleteOrder(target.order.orderId);
        setRows((prev) => prev.filter((r) => r.orderId !== target.order.orderId));
        setRowCount((prev) => prev - 1);
        setSelectionModel((prev) => {
          const ids = new Set(prev.ids);
          ids.delete(target.order.orderId);
          return { ...prev, ids };
        });
        setResultAlert({ severity: "success", message: `Order ${target.order.orderNumber} was deleted.` });
      } else {
        const result = await bulkDeleteOrders(target.orderIds);
        const deletedSet = new Set(result.deletedIds);
        setRows((prev) => prev.filter((r) => !deletedSet.has(r.orderId)));
        setRowCount((prev) => prev - result.deletedIds.length);
        setSelectionModel((prev) => {
          const ids = new Set(prev.ids);
          deletedSet.forEach((id) => ids.delete(id));
          return { ...prev, ids };
        });

        if (result.failures.length === 0) {
          setResultAlert({ severity: "success", message: `${result.deletedIds.length} order(s) deleted.` });
        } else {
          setResultAlert({
            severity: "warning",
            message: `${result.deletedIds.length} deleted, ${result.failures.length} failed: ${result.failures
              .map((f) => `#${f.orderId} (${f.error})`)
              .join(", ")}`,
          });
        }
      }
      setTarget(null);
      setConfirmText("");
    } catch (err) {
      setDialogError(err?.response?.data?.title || err?.response?.data || "Failed to delete. Please try again.");
    } finally {
      setDeleting(false);
    }
  }

  const columns = useMemo(
    () => [
      { field: "orderNumber", headerName: "Order #", width: 170 },
      { field: "storeName", headerName: "Store", flex: 1, minWidth: 160 },
      { field: "customerName", headerName: "Customer", flex: 1, minWidth: 160 },
      { field: "itemCount", headerName: "Items", width: 90 },
      {
        field: "status",
        headerName: "Status",
        width: 160,
        renderCell: (params) => (
          <Chip size="small" label={OrderStatusLabels[params.value]} color={OrderStatusColors[params.value]} />
        ),
      },
      {
        field: "totalAmount",
        headerName: "Total",
        width: 120,
        align: "right",
        headerAlign: "right",
        valueFormatter: (v) => formatCurrency(v),
      },
      {
        field: "createdAt",
        headerName: "Date",
        width: 170,
        valueFormatter: (v) => formatDate(v),
      },
      {
        field: "actions",
        headerName: "",
        width: 70,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <IconButton
            size="small"
            color="error"
            title="Delete order"
            onClick={(e) => {
              e.stopPropagation();
              openSingleDelete(params.row);
            }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        ),
      },
    ],
    []
  );

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4">Delete Orders</Typography>
        <Button
          variant="contained"
          color="error"
          startIcon={<DeleteOutlineIcon />}
          disabled={selectedIds.length === 0}
          onClick={openBulkDelete}
        >
          Delete Selected ({selectedIds.length})
        </Button>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Permanently deletes orders and everything tied to them (items, payments, delivery, fees, coupon usage).
        Use this to clean up development/test orders.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {resultAlert && (
        <Alert severity={resultAlert.severity} sx={{ mb: 2 }} onClose={() => setResultAlert(null)}>
          {resultAlert.message}
        </Alert>
      )}

      <Stack component="form" onSubmit={handleSearchSubmit} direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          label="Search order #, customer or store"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ minWidth: 280 }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">All statuses</MenuItem>
            {Object.entries(OrderStatusLabels).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <Box sx={{ bgcolor: "background.paper", borderRadius: 2 }}>
        <DataGrid
          autoHeight
          checkboxSelection
          disableRowSelectionOnClick
          rows={rows}
          getRowId={(row) => row.orderId}
          columns={columns}
          rowCount={rowCount}
          loading={loading}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={setSelectionModel}
          sx={{ border: "none" }}
        />
      </Box>

      <Dialog open={Boolean(target)} onClose={closeDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          {target?.mode === "single"
            ? `Delete order ${target.order.orderNumber}?`
            : `Delete ${target?.orderIds.length ?? 0} orders?`}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>This is permanent and will also delete:</DialogContentText>
          <List dense disablePadding sx={{ mb: 1 }}>
            {WIPED_ITEMS.map((item) => (
              <ListItem key={item} disablePadding sx={{ pl: 1 }}>
                <ListItemText primary={`• ${item}`} />
              </ListItem>
            ))}
          </List>
          <DialogContentText sx={{ mb: 1 }}>These stay, just unlinked from the order:</DialogContentText>
          <List dense disablePadding sx={{ mb: 2 }}>
            {UNLINKED_ITEMS.map((item) => (
              <ListItem key={item} disablePadding sx={{ pl: 1 }}>
                <ListItemText primary={`• ${item}`} />
              </ListItem>
            ))}
          </List>

          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dialogError}
            </Alert>
          )}

          <TextField
            label={target?.mode === "single" ? `Type "${target.order.orderNumber}" to confirm` : 'Type "DELETE" to confirm'}
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            fullWidth
            size="small"
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleConfirmDelete} disabled={!canConfirm}>
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
